import { Injectable } from '@angular/core';
import { UserStates } from '@lsq-frontend/fasttrack-shared/app-constants';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { UserStateService } from '@lsq-frontend/fasttrack-shared/features/user-state-service';
import {
    Guid,
    InvoiceBatchImage,
    InvoiceBatchInvoiceType,
    UserStateStatus
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    ListInvoiceBatchImagesService,
    SendMobileUploadLinkRequest,
    SendMobileUploadLinkService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { Observable } from 'rxjs';
import { ProgressState } from 'rxjs-uploader';
import { map, shareReplay } from 'rxjs/operators';
import { AnalyticsEvent } from '../../../constants/analytics.constants';
import { InvoiceFileUpload } from '../invoices.models';
import { InvoiceBatchService } from './invoice-batch.service';
import { FakeFile } from './upload-invoices.service';

@Injectable({ providedIn: 'root' })
export class SmsUploadService {
    public invoiceFileUploads: InvoiceFileUpload[] = [];
    public mobileUploadsStream: Observable<InvoiceFileUpload[]>;

    constructor(
        public invoiceBatchService: InvoiceBatchService,
        private _analyticsService: AnalyticsService,
        private _sendMobileUploadLinkService: SendMobileUploadLinkService,
        private _listInvoiceBatchImagesService: ListInvoiceBatchImagesService,
        private _userStateService: UserStateService,
        private _appSettings: AppSettings
    ) {}

    public async sendMessage(request: SendMobileUploadLinkRequest): Promise<void> {
        this._analyticsService.triggerSimpleEvent(AnalyticsEvent.MobileUploadSendSms);
        await this._sendMobileUploadLinkService.executeOnce(request);
        this.updateMobileNumberState(request.phone);
    }

    public updateMobileNumberState(phoneNumber: string): void {
        this._userStateService.createOrUpdateUserState(
            UserStates.MobileUploadNumber,
            UserStateStatus.Done,
            { phoneNumber }
        );
    }

    public async getMobileNumberState(): Promise<string> {
        const mobileNumberUserState = await this._userStateService.getUserState(
            UserStates.MobileUploadNumber
        );
        if (mobileNumberUserState?.jsonData) {
            return JSON.parse(mobileNumberUserState.jsonData).phoneNumber;
        }
    }

    // Stream of already-executed file uploads to be displayed in the UI.
    // In this case, any files uploaded using the mobile upload link should appear in this stream.
    public getCompletedUploadsStream(
        masterInvoiceBatchKey?: Guid
    ): Observable<InvoiceFileUpload[]> {
        return this._listInvoiceBatchImagesService.dataStream.pipe(
            map((response) => {
                const newImages = response.rows.filter((image: InvoiceBatchImage) => {
                    const isImageInRemovedArray = this.invoiceBatchService.removedMobileUploads.find(
                        (upload) => upload.imageKey === image.invoiceBatchImageKey
                    );
                    return (
                        !isImageInRemovedArray &&
                        image.invoiceBatchInvoiceType !== InvoiceBatchInvoiceType.Summary
                    );
                });

                if (newImages.length) {
                    return newImages
                        .map((newImage: InvoiceBatchImage) => {
                            const fakeFile = new FakeFile(newImage.fileName) as File;
                            const fileUpload = new InvoiceFileUpload(
                                fakeFile,
                                Symbol(`mobile-upload-${newImage.invoiceBatchImageKey}`)
                            );
                            fileUpload.imageKey = newImage.invoiceBatchImageKey;
                            fileUpload.previewUrl = newImage.url;
                            fileUpload.isMobileUpload = true;
                            fileUpload.isUploadStatusComplete = true;
                            fileUpload.progress = {
                                percent: 100,
                                state: ProgressState.Completed
                            };
                            fileUpload.masterInvoiceBatchKey = response.masterInvoiceBatchKey;
                            return fileUpload;
                        })
                        .sort(
                            (a, b) =>
                                // Make sure that if the images have been reordered, we keep that order.
                                this.invoiceFileUploads.indexOf(a) -
                                this.invoiceFileUploads.indexOf(b)
                        );
                }
                return [];
            }),
            // Make sure this stream of completed uploads is included in the fileUploads stream.
            shareReplay(1)
        );
    }
}
